import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import LockClockIcon from '@mui/icons-material/LockClock'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiQuestionToEditable, type ApiQuestion } from '@/components/questions/apiAdapter'
import { ApiError } from '@/services/baseService'
import { useEdition } from '@/hooks/useEdition'
import { useSubscription } from '@/hooks/useSubscription'
import { projectService, type QuestionnaireTemplate } from '@/services/projectService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import WizardStepper, { type WizardStep } from '@/components/researcher/wizard/WizardStepper'
import WizardStepBasics from '@/components/researcher/wizard/WizardStepBasics'
import WizardStepStimulus from '@/components/researcher/wizard/WizardStepStimulus'
import WizardStepQuestions from '@/components/researcher/wizard/WizardStepQuestions'
import WizardStepDistribution from '@/components/researcher/wizard/WizardStepDistribution'
import BasicStudySetup from '@/components/researcher/wizard/BasicStudySetup'
import LaunchedDialog from '@/components/researcher/wizard/LaunchedDialog'
import { useWizardDraft } from '@/components/researcher/wizard/useWizardDraft'
import { buildBasicLaunchPlan, buildProLaunchPlan } from '@/components/researcher/wizard/launchPlan'
import { selectedMetric, useFixedMetric } from '@/components/researcher/wizard/fixedMetric'
import { MAX_STIMULUS_MINUTES, basicReadyToLaunch } from '@/components/researcher/wizard/basicStudy'
import type { StudyDraft } from '@/components/researcher/wizard/types'
import { figmaErrorText } from '@/components/researcher/wizard/FigmaStimulusEditor'
import LivePreview, { type PreviewScreen } from '@/components/researcher/wizard/LivePreview'
import { FORM_MAX, outlinedBtnSx, primaryBtnSx } from '@/components/researcher/wizard/deskStyles'
import { askableQuestions } from '@/components/researcher/wizard/askable'
import { formatDuration, totalStimulusSeconds } from '@/components/researcher/wizard/estimate'

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)

const TOTAL_STEPS = 4

/** Form column + phone, centred in the page. */
const PAGE_MAX = 1140

/**
 * One route, two products. FaceWise Pro walks its four steps; FaceWise sets a
 * study up on a single screen. They share the draft machinery in
 * `useWizardDraft` — every entry point in the app already lands here, and a
 * second page would only rot beside this one.
 */
export default function CreateStudyWizard() {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { isBasic } = useEdition()
  const { isExpired, plan } = useSubscription()
  const fixedMetric = useFixedMetric()

  const [step, setStep] = useState(0)
  const [templates, setTemplates] = useState<QuestionnaireTemplate[]>([])
  // Which refusal the snackbar is currently showing, so it can offer the
  // matching way out. They are not the same problem and must not share a
  // button: an ended plan is bought back with a plan, a spent package with more
  // respondents on the plan you already have, and refused material with a mail
  // to a person — the content check blocks rather than queueing, so the appeal
  // is the only way the decision gets looked at again.
  const [refusal, setRefusal] = useState<'plan' | 'package' | 'appeal' | null>(null)
  /** Where an appeal against the content check goes; the backend names it. */
  const [appealEmail, setAppealEmail] = useState<string | null>(null)

  const stepLabels = useMemo(
    () => [
      t('wizard.steps.basics'),
      t('wizard.steps.stimulus'),
      t('wizard.steps.questions'),
      t('wizard.steps.distribution'),
    ],
    [t],
  )

  /** A failed launch, in words the customer can act on. */
  const describeError = useCallback(
    (err: unknown): string => {
      setRefusal(null)
      setAppealEmail(null)
      if (err instanceof ApiError) {
        switch (err.code) {
          // Both editions can hit this now: signing up starts a fourteen-day
          // trial of FaceWise, so "the plan ended" is a sentence every customer can
          // be told. It used to be rewritten as "you ran out of respondents"
          // for FaceWise, which was true then and would be a lie now — the two
          // arrive as different codes and mean different things.
          case 'trial_expired':
            setRefusal('plan')
            return t('trial.launchBlocked')
          // Nothing is wrong with the study — the address on the account has
          // never answered (Z-72). Saying "launch failed" here would send
          // somebody digging through their questions for a fault that is not
          // in them.
          case 'email_not_verified':
            return t('wizard.errors.emailNotVerified')
          case 'respondent_quota_exhausted':
            setRefusal('package')
            return t('wizard.errors.respondentsExhausted')
          case 'stimulus_awaiting_review':
            return t('wizard.errors.stimulusAwaitingReview')
          case 'stimulus_rejected': {
            // Optional, and it says so: the study is not waiting on the letter
            // being written. Without an address from the backend the sentence
            // stands on its own rather than offering a dead button.
            const email = err.data?.appeal_email as string | undefined
            if (email) {
              setRefusal('appeal')
              setAppealEmail(email)
            }
            return t('wizard.errors.stimulusRejected')
          }
          // Figma prototype (Z-98): the link, the connection or the goal.
          case 'figma_bad_url':
          case 'figma_not_connected':
          case 'figma_no_access':
          case 'figma_not_found':
          case 'figma_empty':
          case 'figma_bad_goal':
          case 'figma_task_missing':
          case 'figma_unreachable':
          case 'figma_unavailable':
            return t(`wizard.errors.figma.${err.code}`)
          case 'figma_rate_limited':
            return figmaErrorText(t, err) ?? err.message
          case 'stimulus_too_long':
            return t('wizard.errors.stimulusTooLong', {
              minutes: (err.data?.max_minutes as number | undefined) ?? MAX_STIMULUS_MINUTES,
            })
        }
      }
      return err instanceof Error ? err.message : 'Error'
    },
    [t],
  )

  const buildLaunchPlan = useCallback(
    (draft: StudyDraft) =>
      isBasic
        ? buildBasicLaunchPlan(draft, selectedMetric(fixedMetric, draft.metricFields), t('wizard.newStudy'))
        : buildProLaunchPlan(draft, stepLabels[0]),
    [isBasic, fixedMetric, stepLabels, t],
  )

  const wizard = useWizardDraft({
    buildLaunchPlan,
    describeError,
    draftLoadError: t('wizard.draftLoadError', { defaultValue: 'Could not load the draft.' }),
    confirmDelete: (name) => t('wizard.footer.deleteDraftConfirm', { name }),
    deleteFailed: t('wizard.footer.deleteDraftFailed'),
  })
  const { draft, patchDraft, launching, deleting, saveState, draftId, error, setError } = wizard

  // Templates only exist in Pro — FaceWise never authors its own questions.
  useEffect(() => {
    if (isBasic) return
    let cancelled = false
    projectService
      .getQuestionnaireTemplates()
      .then((data) => !cancelled && setTemplates(data))
      .catch(() => !cancelled && setTemplates([]))
    return () => {
      cancelled = true
    }
  }, [isBasic])

  // "Use in a new study" on the templates screen lands here with ?template=<id>:
  // a fresh draft starts with that template's questions already in step 3.
  const [searchParams] = useSearchParams()
  const templateParam = searchParams.get('template')
  const appliedTemplate = useRef(false)
  useEffect(() => {
    if (isBasic || !templateParam || searchParams.get('draft') || appliedTemplate.current) return
    appliedTemplate.current = true
    projectService
      .getQuestionnaireTemplate(Number(templateParam))
      .then((tpl) =>
        patchDraft({
          questions: ((tpl.questions ?? []) as unknown as ApiQuestion[]).map(apiQuestionToEditable),
          questionnaireTemplateId: tpl.id,
        }),
      )
      .catch(() => undefined)
  }, [isBasic, templateParam, searchParams, patchDraft])

  const dismissError = () => {
    setError(null)
    setRefusal(null)
    setAppealEmail(null)
  }

  const handleDeleteDraft = () => void wizard.deleteDraft(t('wizard.newStudy'))
  const handleLaunch = () => void wizard.launch()

  const canProceed = isBasic ? basicReadyToLaunch(draft) : step === 0 ? draft.name.trim().length > 0 : true
  const isLastStep = isBasic || step === TOTAL_STEPS - 1

  const handleNext = () => {
    if (!isBasic && step < TOTAL_STEPS - 1) setStep((s) => s + 1)
    else handleLaunch()
  }

  const handleBack = () => {
    if (!isBasic && step > 0) setStep((s) => s - 1)
    else navigate('/dashboard')
  }

  const saveIndicator = (() => {
    if (saveState === 'saving') return { text: t('wizard.saving', { defaultValue: 'Zapisywanie…' }), dot: c.coral }
    if (saveState === 'error') return { text: t('wizard.saveError', { defaultValue: 'Nie zapisano — spróbuję ponownie' }), dot: c.danger }
    if (saveState === 'saved') return { text: t('wizard.saved', { defaultValue: 'Zapisano wersję roboczą' }), dot: c.success }
    return { text: t('wizard.autosave'), dot: c.success }
  })()

  const nameMissing = draft.name.trim().length === 0
  const nextLabel = isLastStep
    ? t(launching ? 'wizard.footer.launching' : 'wizard.footer.launch')
    : t('wizard.desk.nextTo', { step: stepLabels[step + 1] })

  // ⌘↵ / Ctrl+↵ moves on (or launches) from anywhere in the wizard, a text
  // field included — the same thing the primary button does, under the same rule.
  const nextRef = useRef<() => void>(() => {})
  nextRef.current = () => {
    if (canProceed && !launching && !deleting) handleNext()
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        nextRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // A new step starts at its top, not wherever the previous one was scrolled to.
  const topRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' })
  }, [step])

  const steps: WizardStep[] = useMemo(() => {
    const seconds = totalStimulusSeconds(draft.stimuli)
    const asked = askableQuestions(draft.questions, draft.demographics).length
    return [
      { label: stepLabels[0], summary: draft.name.trim() },
      {
        label: stepLabels[1],
        summary: draft.stimuli.length ? (seconds > 0 ? `${draft.stimuli.length} · ${formatDuration(seconds)}` : String(draft.stimuli.length)) : '',
      },
      { label: stepLabels[2], summary: asked ? String(asked) : '' },
      { label: stepLabels[3] },
    ]
  }, [stepLabels, draft.name, draft.stimuli, draft.questions, draft.demographics])

  const saveStatus = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: 12.5, color: c.ink56, whiteSpace: 'nowrap' }}>
      {saveState === 'saving' ? (
        <CircularProgress size={10} thickness={6} sx={{ color: saveIndicator.dot }} />
      ) : (
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: saveIndicator.dot }} />
      )}
      {saveIndicator.text}
    </Box>
  )

  // Steps and the save state share one bar, aligned with the form below it.
  const subBar = (
    <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${c.ink08}`, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: PAGE_MAX, mx: 'auto', display: 'flex', alignItems: 'center', gap: 3, minHeight: 56 }}>
        {/* FaceWise has nothing to step through, so the bar carries only the
            autosave state — kept visible so the customer still sees it is saved. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!isBasic && (
            <WizardStepper
              steps={steps}
              current={step}
              onSelect={setStep}
              // Any step opens once the study has a name — the one thing step 1 requires.
              canSelect={() => !nameMissing}
            />
          )}
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>{saveStatus}</Box>
      </Box>
    </Box>
  )

  // A light, translucent bar: the form scrolls under it rather than ending at a wall.
  const footer = (
    <Box
      component="footer"
      sx={{
        px: { xs: 2, md: 4 },
        py: 1.5,
        bgcolor: 'rgba(255,255,255,.82)',
        backdropFilter: 'saturate(180%) blur(20px)',
        borderTop: `1px solid ${c.ink08}`,
        '@media (prefers-reduced-transparency: reduce)': { bgcolor: '#fff', backdropFilter: 'none' },
      }}
    >
      <Box sx={{ maxWidth: PAGE_MAX, mx: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          color="inherit"
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          onClick={handleBack}
          sx={{ color: c.ink72, fontWeight: 600, fontSize: 14, borderRadius: '10px', height: 36, px: 1.5 }}
        >
          {isBasic || step === 0 ? t('wizard.footer.cancel') : t('wizard.footer.back')}
        </Button>
        {draftId != null && (
          <Button
            color="inherit"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
            onClick={handleDeleteDraft}
            disabled={deleting}
            sx={{ color: c.ink48, fontSize: 14, borderRadius: '10px', height: 36, px: 1.5, display: { xs: 'none', sm: 'inline-flex' }, '&:hover': { color: c.danger } }}
          >
            {t(deleting ? 'wizard.footer.deletingDraft' : 'wizard.footer.deleteDraft')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {!isBasic && step === 0 && nameMissing && (
          <Typography sx={{ fontSize: 13, color: c.ink56, mr: 1, display: { xs: 'none', md: 'block' } }}>{t('wizard.desk.nameRequired')}</Typography>
        )}
        <Button onClick={() => void wizard.saveDraftAndExit()} disabled={saveState === 'saving' || deleting} sx={outlinedBtnSx}>
          {t('wizard.desk.saveAndClose')}
        </Button>
        <Button
          variant="contained"
          disabled={!canProceed || launching || deleting}
          onClick={handleNext}
          title={`${IS_MAC ? '⌘' : 'Ctrl'} ↵`}
          endIcon={isLastStep ? <PlayArrowIcon sx={{ fontSize: 18 }} /> : <ArrowForwardIcon sx={{ fontSize: 17 }} />}
          sx={{ ...primaryBtnSx, px: 2.25 }}
        >
          {nextLabel}
        </Button>
      </Box>
    </Box>
  )

  /** Which respondent screen each step is about. */
  const previewScreen: PreviewScreen = step === 1 ? 'stimulus' : step === 2 ? 'questions' : step === 3 && draft.requireAccessCode ? 'consent' : 'landing'

  // Trial/plan expired — show the plans instead of the wizard. This is the
  // choke point for every entry route (TopBar, dashboard cards, direct
  // /studies/new navigation, draft-resume links) and keeps the autosave POST
  // from ever firing. The backend enforces the same gate on the API.
  //
  // Both editions land here. This used to be a Pro-only screen on the rule
  // that FaceWise was not sold by the day; a signup is now a fourteen-day
  // FaceWise trial, which makes an expired FaceWise the ordinary case rather
  // than an impossible one.
  if (isExpired) {
    return (
      <ResearcherShell active="studies" crumbs={[t('nav.studies'), t('wizard.newStudy')]} hideTopBarAction>
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
          <Box
            sx={{
              maxWidth: 460,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: c.coralSoft,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <LockClockIcon sx={{ fontSize: 28, color: c.coral }} />
            </Box>
            <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 22 }}>
              {plan === 'full' ? t('trial.subscriptionExpiredTitle') : t('trial.expiredTitle')}
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: c.ink72, lineHeight: 1.55 }}>
              {t('trial.expiredBody')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/plans')}
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              >
                {t('trial.cta')}
              </Button>
              <Button onClick={() => navigate('/dashboard')} sx={{ color: c.ink72 }}>
                {t('trial.backToDashboard')}
              </Button>
            </Box>
          </Box>
        </Box>
      </ResearcherShell>
    )
  }

  return (
    <ResearcherShell
      active="studies"
      crumbs={[t('nav.studies'), draft.name.trim() || t('wizard.newStudy')]}
      hideTopBarAction
      subBar={subBar}
      footer={footer}
    >
      {isBasic ? (
        <BasicStudySetup
          draft={draft}
          onChange={patchDraft}
          onPatchStimulus={wizard.patchStimulus}
          onRemoveStimulus={wizard.removeStimulus}
          onLaunch={handleLaunch}
          launching={launching}
        />
      ) : (
        <Box
          ref={topRef}
          sx={{
            maxWidth: PAGE_MAX,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: `minmax(0, ${FORM_MAX}px) minmax(0, 1fr)` },
            columnGap: { lg: 8 },
            rowGap: 6,
            alignItems: 'start',
            scrollMarginTop: 28,
            pt: { md: 2 },
            pb: 6,
          }}
        >
          <Box sx={{ minWidth: 0, width: '100%', maxWidth: FORM_MAX, mx: { xs: 'auto', lg: 0 } }}>
            {step === 0 && <WizardStepBasics draft={draft} onChange={patchDraft} />}
            {step === 1 && <WizardStepStimulus draft={draft} onChange={patchDraft} onPatchStimulus={wizard.patchStimulus} />}
            {step === 2 && <WizardStepQuestions draft={draft} onChange={patchDraft} templates={templates} />}
            {step === 3 && <WizardStepDistribution draft={draft} onChange={patchDraft} />}
          </Box>
          {/* The respondent's real screens in a phone. Pinned beside the form;
              under it on narrower windows. */}
          <Box component="aside" sx={{ minWidth: 0, display: 'flex', justifyContent: 'center', position: { lg: 'sticky' }, top: { lg: 8 } }}>
            <LivePreview draft={draft} initialScreen={previewScreen} />
          </Box>
        </Box>
      )}

      {/* A refused launch stays up until it is dismissed when there is
          something to do about it — six seconds is not long enough to read a
          sentence and decide to spend money on it. */}
      <Snackbar
        open={!!error}
        autoHideDuration={refusal ? null : 6000}
        onClose={dismissError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={dismissError} sx={{ width: '100%' }}>
          <Typography variant="body2">{error}</Typography>
          {refusal === 'appeal' && appealEmail ? (
            <Button
              size="small"
              component="a"
              href={`mailto:${appealEmail}?subject=${encodeURIComponent(
                t('wizard.errors.appealSubject'),
              )}&body=${encodeURIComponent(t('wizard.errors.appealBody', { name: draft.name }))}`}
              sx={{ mt: 0.5, px: 0, fontWeight: 700 }}
            >
              {t('wizard.errors.appeal')}
            </Button>
          ) : refusal ? (
            <Button
              size="small"
              onClick={() => navigate('/plans')}
              sx={{ mt: 0.5, px: 0, fontWeight: 700 }}
            >
              {refusal === 'plan' ? t('trial.cta') : t('wizard.errors.topUp')}
            </Button>
          ) : null}
        </Alert>
      </Snackbar>

      <LaunchedDialog
        shareLink={wizard.shareLink}
        onClose={() => navigate(wizard.launchedId ? `/studies/${wizard.launchedId}` : '/dashboard')}
      />
    </ResearcherShell>
  )
}
